import React, { useState } from 'react';
import { useOrder } from '../../context/OrderContext';

const DrainageSelector = ({ onSelect, initialValues = {}, isEditMode = false }) => {
    const { state } = useOrder();
    const [needsDrainage, setNeedsDrainage] = useState(isEditMode ? true : null);
    const [drainageType, setDrainageType] = useState(initialValues.drainageType || null);

    // Get the selected profile to check valid drainage options
    const profileType = state.orderDetails.profileType || state.orderDetails.ninfaNumber || '';

    // Normalize the profile type to handle both "56" and "N56" formats
    const normalizedProfile = profileType.startsWith('N') ? profileType : `N${profileType}`;

    // Check which models are valid for each drainage type
    const isN116 = normalizedProfile === 'N116';
    const isValidForAllDrainage = ['N6', 'N56', 'N106'].includes(normalizedProfile);

    // Define drainage types with validation rules based on profile
    const allDrainageTypes = [
        {
            id: 'foro-int-est',
            label: 'FORO INT-EST',
            image: 'foro-int-est',
            isValid: isValidForAllDrainage // N6, N56, N106
        },
        {
            id: 'foro-int',
            label: 'FORO INT',
            image: 'foro-int',
            isValid: isValidForAllDrainage // N6, N56, N106
        },
        {
            id: 'foro-est',
            label: 'FORO EST',
            image: 'foro-est',
            isValid: isValidForAllDrainage || isN116 // N6, N56, N106, N116
        },
        {
            id: 'fresatura',
            label: 'FRESATURA',
            image: 'fresatura',
            isValid: isValidForAllDrainage // N6, N56, N106
        },
        {
            id: 'fresatura-tappo',
            label: 'FRESATURA CON TAPPO',
            image: 'fresatura-tappo',
            isValid: isValidForAllDrainage // N6, N56, N106
        }
    ];

    // Filter only valid drainage types for the selected profile
    const drainageTypes = allDrainageTypes.filter(type => type.isValid);

    const handleSubmit = () => {
        if (isEditMode && drainageType) {
            // In edit mode, we always have drainage and just need to confirm the type
            onSelect({
                needsDrainage: true,
                drainageType
            });
        } else if (needsDrainage === false || (needsDrainage && drainageType)) {
            // In create mode, check if drainage is needed
            onSelect({
                needsDrainage,
                drainageType: needsDrainage ? drainageType : null
            });
        }
    };

    return (
        <div className="space-y-6">
            {!isEditMode && (
                <>
                    <h2 className="text-xl font-semibold text-center mb-6">
                        VUOI FORO O FRESATURA SCARICO ACQUA?
                    </h2>
                    <p style={{ textAlign: 'center' }}>
                        Il foro serve per lo scarico dell’acqua interna al profilo.<br />
                        La fresatura serve per lo scarico ed il deflusso delle acque piovane verso l’esterno.
                    </p>
                    {/* Yes/No Selection - Only show in create mode */}
                    <div className="flex justify-center space-x-4">
                        <button
                            onClick={() => {
                                setNeedsDrainage(true);
                                setDrainageType(null);
                            }}
                            className={`flex-1 py-3 px-6 border-2 rounded-lg
                    ${needsDrainage === true ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                        >
                            SI
                        </button>
                        <button
                            onClick={() => {
                                setNeedsDrainage(false);
                                setDrainageType(null);
                            }}
                            className={`flex-1 py-3 px-6 border-2 rounded-lg
                    ${needsDrainage === false ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                        >
                            NO
                        </button>
                    </div>
                </>
            )}

            {/* Drainage Type Selection - Always show in edit mode */}
            {(needsDrainage || isEditMode) && (
                <div className="space-y-4 mt-6">
                    {!isEditMode && (
                        <h3 className="text-lg font-medium text-center mb-4">
                            FORO O FRESATURA SCARICO ACQUA
                        </h3>
                    )}

                    {drainageTypes.length === 0 ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-yellow-800 text-center">
                                Il profilo selezionato ({normalizedProfile || 'nessuno'}) non supporta opzioni di scarico acqua.
                                <br />
                                Le opzioni di scarico sono disponibili solo per i profili: N6, N56, N106, N116 (solo foro esterno).
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-4">
                            {drainageTypes.map((type) => (
                                <button
                                    key={type.id}
                                    onClick={() => setDrainageType(type.id)}
                                    className={`p-4 border-2 rounded-lg text-center
                      ${drainageType === type.id ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                >
                                    <div className="aspect-w-1 aspect-h-1 mb-2">
                                        {/* Replace with actual images */}
                                        <div className="w-full h-32 bg-gray-100 rounded-lg mb-2"></div>
                                    </div>
                                    <span className="text-sm font-medium">{type.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
                <button
                    onClick={handleSubmit}
                    disabled={isEditMode ? !drainageType : (needsDrainage === null || (needsDrainage && !drainageType))}
                    className="w-full py-2 px-4 bg-red-500 text-white rounded-lg 
                   disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isEditMode ? 'Conferma' : 'Avanti'}
                </button>
            </div>
        </div>
    );
};

export default DrainageSelector;
